import { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Page, Header } from 'components';
import { Card, CardContent } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  nrImportFile,
  nrGetLastResult,
  nrSendEmail,
  nrGetLastUpdate,
} from '../actions/naspReportingThunks';
import './NaspReporting.css';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  container: {
    marginTop: theme.spacing(3),
  },
}));

// ─── ICONS ────────────────────────────────────────────────────────────────────
const Icon = {
  Upload: ({ size = 16, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
    </svg>
  ),
  Download: ({ size = 16, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  ),
  Mail: ({ size = 16, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2"/><polyline points="2 7 12 13 22 7"/>
    </svg>
  ),
  User: ({ size = 13, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
    </svg>
  ),
  Time: ({ size = 13, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
    </svg>
  ),
  X: ({ size = 16, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  Csv: () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" stroke="#0e7490" strokeWidth="1.5"/>
      <polyline points="13 2 13 9 20 9" stroke="#0e7490" strokeWidth="1.5"/>
      <line x1="8" y1="13" x2="16" y2="13" stroke="#0e7490" strokeWidth="1.5"/>
      <line x1="8" y1="17" x2="16" y2="17" stroke="#0e7490" strokeWidth="1.5"/>
    </svg>
  ),
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const formatTimestamp = (value) => {
  if (!value || value === '0001-01-01T00:00:00' || value === '0001-01-01') return '';
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString('pt-PT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    });
  } catch { return ''; }
};

// ─── REPORTS ──────────────────────────────────────────────────────────────────
// `report` is the discriminator sent to the API and the key used in state.
const REPORTS = [
  {
    report:      'ThreeMonthComparison',
    title:       '3 Month Comparison',
    badge:       { label: 'Report', background: '#eff6ff', color: '#1d4ed8' },
    iconColor:   '#1d4ed8',
    iconBg:      '#eff6ff',
  },
  {
    report:      'Cancellations',
    title:       'Cancellations',
    badge:       { label: 'Report', background: '#faf5ff', color: '#7c3aed' },
    iconColor:   '#7c3aed',
    iconBg:      '#faf5ff',
    // only this report can be emailed
    sendEmail:   true,
  },
];

// ─── SPINNER ──────────────────────────────────────────────────────────────────
function Spinner({ dark = false }) {
  return <span className={`nr-spinner${dark ? ' nr-spinner-dark' : ''}`} />;
}

// ─── ACTION BUTTON ────────────────────────────────────────────────────────────
function ActionButton({ iconColor, iconBg, IconComp, label, description, onClick, loading, disabled }) {
  return (
    <button className="nr-action-btn" onClick={onClick} disabled={loading || disabled}>
      <div className="nr-action-icon" style={{ background: iconBg }}>
        {loading ? <Spinner dark /> : <IconComp size={16} color={iconColor} />}
      </div>
      <span className="nr-action-btn-label">{label}</span>
      {description && <span className="nr-action-btn-desc">{description}</span>}
    </button>
  );
}

// ─── LAST UPDATE ─────────────────────────────────────────────────────────────
function LastUpdate({ user, timestamp }) {
  const formatted = formatTimestamp(timestamp);
  const hasData   = user || formatted;
  const rows = [
    ...(user      ? [{ Icon: Icon.User, text: user }]      : []),
    ...(formatted ? [{ Icon: Icon.Time, text: formatted }] : []),
  ];

  return (
    <div>
      <p className="nr-last-update-label">Last update</p>
      {hasData ? rows.map(({ Icon: Ic, text }, i) => (
        <div key={i} className="nr-last-update-row">
          <span className="nr-last-update-row-icon"><Ic /></span>
          <span className="nr-last-update-row-text">{text}</span>
        </div>
      )) : (
        <p className="nr-no-data">No data available</p>
      )}
    </div>
  );
}

// ─── UPLOAD MODAL ─────────────────────────────────────────────────────────────
function UploadModal({ title, subtitle, onClose, onConfirm, loading }) {
  const [file, setFile]         = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError]       = useState('');
  const inputRef                = useRef(null);

  const MAX_SIZE_MB    = 100;
  const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

  const isValid = (f) => f.name.endsWith('.csv');

  const applyFile = (f) => {
    if (!f) return;
    if (!isValid(f)) { setError('Only .csv files are accepted.'); return; }
    if (f.size > MAX_SIZE_BYTES) { setError(`File size exceeds the ${MAX_SIZE_MB}MB limit.`); return; }
    setError(''); setFile(f);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault(); setDragOver(false); applyFile(e.dataTransfer.files[0]);
  }, []);

  // Rendered into document.body: the fade animation on .nr-root leaves a
  // transform behind, which would make `position: fixed` resolve against that
  // element and get clipped by the MUI Card's overflow.
  return createPortal(
    <div className="nr-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="nr-modal">
        <div className="nr-modal-header">
          <div>
            <p className="nr-modal-title">{title}</p>
            {subtitle && <p className="nr-modal-subtitle">{subtitle}</p>}
          </div>
          <button className="nr-close-btn" onClick={onClose}><Icon.X /></button>
        </div>

        <div className="nr-modal-body">
          <div
            className={`nr-dropzone${dragOver ? ' drag-over' : ''}${file ? ' has-file' : ''}`}
            onClick={() => !file && inputRef.current.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
          >
            {file ? (
              <div className="nr-file-info">
                <Icon.Csv />
                <p className="nr-file-name">{file.name}</p>
                <p className="nr-file-size">{(file.size / 1024).toFixed(1)} KB</p>
                <button className="nr-remove-btn" onClick={(e) => { e.stopPropagation(); setFile(null); setError(''); }}>
                  Remove
                </button>
              </div>
            ) : (
              <>
                <Icon.Upload size={28} color="#94a3b8" />
                <p className="nr-dropzone-title">{dragOver ? 'Drop to upload' : 'Drag & drop or click to browse'}</p>
                <p className="nr-dropzone-sub">.csv — max 100MB</p>
              </>
            )}
          </div>
          <input
            ref={inputRef} type="file"
            accept=".csv,text/csv"
            onChange={(e) => applyFile(e.target.files[0])}
            style={{ display: 'none' }}
          />
          {error && <p className="nr-error-msg">{error}</p>}
        </div>

        <div className="nr-modal-footer">
          <button className="nr-btn nr-btn-ghost" onClick={onClose}>Cancel</button>
          <button
            className="nr-btn nr-btn-primary"
            onClick={() => file && onConfirm(file)}
            disabled={!file || loading}
          >
            {loading ? <><Spinner /> Uploading...</> : 'Upload'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── SEND EMAIL MODAL ─────────────────────────────────────────────────────────
// Test goes to the test recipients, Final to the client distribution list.
// Defaults to Test so the client list is never a single misclick away.
const EMAIL_MODES = [
  { value: 'test',  label: 'Test email',  description: 'Sends to the test recipients only' },
  { value: 'final', label: 'Final email', description: 'Sends to the client distribution list' },
];

function SendEmailModal({ title, onClose, onConfirm, loading }) {
  const [mode, setMode] = useState('test');
  const isFinal = mode === 'final';

  return createPortal(
    <div className="nr-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="nr-modal">
        <div className="nr-modal-header">
          <div>
            <p className="nr-modal-title">{title}</p>
            <p className="nr-modal-subtitle">Choose the recipients before sending</p>
          </div>
          <button className="nr-close-btn" onClick={onClose}><Icon.X /></button>
        </div>

        <div className="nr-modal-body">
          <div className="nr-options">
            {EMAIL_MODES.map((m) => (
              <button
                key={m.value}
                className={`nr-option${mode === m.value ? ' is-selected' : ''}`}
                onClick={() => setMode(m.value)}
                type="button"
              >
                <span className={`nr-radio${mode === m.value ? ' is-selected' : ''}`} />
                <span className="nr-option-text">
                  <span className="nr-option-label">{m.label}</span>
                  <span className="nr-option-desc">{m.description}</span>
                </span>
              </button>
            ))}
          </div>

          {isFinal && (
            <p className="nr-warning-msg">
              This will reach the client. Make sure the latest result is correct.
            </p>
          )}
        </div>

        <div className="nr-modal-footer">
          <button className="nr-btn nr-btn-ghost" onClick={onClose}>Cancel</button>
          <button
            className={`nr-btn ${isFinal ? 'nr-btn-danger' : 'nr-btn-primary'}`}
            onClick={() => onConfirm(mode === 'test')}
            disabled={loading}
          >
            {loading ? <><Spinner /> Sending...</> : (isFinal ? 'Send to client' : 'Send test')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────
function NaspReporting() {
  const classes       = useStyles();
  const dispatch      = useDispatch();
  const loading       = useSelector((state) => state.naspReporting.loading);
  const naspReporting = useSelector((state) => state.naspReporting.naspReporting);
  const userId        = useSelector((state) => state.session.user?.id);
  const userEmail     = useSelector((state) => state.session.user?.email);

  // report key of the card whose upload modal is open, or null
  const [modal, setModal] = useState(null);
  // report key of the card whose send-email modal is open, or null
  const [emailModal, setEmailModal] = useState(null);

  useEffect(() => {
    dispatch(nrGetLastUpdate());
  }, []);

  const handleImport = (report) => (file) => {
    dispatch(nrImportFile(userId, report, file))
      .then(() => dispatch(nrGetLastUpdate()));
    setModal(null);
  };

  const handleSendEmail = (report) => (isTest) => {
    dispatch(nrSendEmail(userId, userEmail, report, isTest));
    setEmailModal(null);
  };

  const modalReport      = REPORTS.find((r) => r.report === modal);
  const emailModalReport = REPORTS.find((r) => r.report === emailModal);

  return (
    <Page className={classes.root} title="NASP Reporting">
      <Header module="NASP" view="NASP Reporting" />
      <Card className={classes.container}>
        <CardContent style={{ padding: 0 }}>
          <div className="nr-root">

            {modalReport && (
              <UploadModal
                title={`Import ${modalReport.title} file`}
                subtitle="Only .csv files accepted — max 100MB"
                onClose={() => setModal(null)}
                onConfirm={handleImport(modalReport.report)}
                loading={loading.importFile === modalReport.report}
              />
            )}

            {emailModalReport && (
              <SendEmailModal
                title={`Send ${emailModalReport.title} by email`}
                onClose={() => setEmailModal(null)}
                onConfirm={handleSendEmail(emailModalReport.report)}
                loading={loading.sendEmail === emailModalReport.report}
              />
            )}

            <div style={{ maxWidth: 1300, margin: '0 auto', padding: '32px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* ── Two cards side by side ────────────────────────────────── */}
              <div className="nr-grid">
                {REPORTS.map((r) => (
                  <div className="nr-card" key={r.report}>
                    <div className="nr-card-header">
                      <span className="nr-card-title">{r.title}</span>
                      <span className="nr-badge" style={{ background: r.badge.background, color: r.badge.color }}>
                        {r.badge.label}
                      </span>
                    </div>
                    <div className="nr-card-body">
                      <div className="nr-card-layout">
                        <div className="nr-actions">
                          <ActionButton
                            iconColor={r.iconColor} iconBg={r.iconBg} IconComp={Icon.Upload}
                            label="Import file"
                            description={r.disabled ? 'Not available yet' : 'Upload a new .csv file'}
                            onClick={() => setModal(r.report)}
                            loading={loading.importFile === r.report}
                            disabled={r.disabled}
                          />
                          <ActionButton
                            iconColor="#0f766e" iconBg="#f0fdfa" IconComp={Icon.Download}
                            label="Last Result"
                            description={r.disabled ? 'Not available yet' : 'Download the latest result'}
                            onClick={() => dispatch(nrGetLastResult(userId, r.report))}
                            loading={loading.lastResult === r.report}
                            disabled={r.disabled}
                          />
                          {r.sendEmail && (
                            <ActionButton
                              iconColor="#b45309" iconBg="#fffbeb" IconComp={Icon.Mail}
                              label="Send Email"
                              description={r.disabled ? 'Not available yet' : 'Send a test or the final email'}
                              onClick={() => setEmailModal(r.report)}
                              loading={loading.sendEmail === r.report}
                              disabled={r.disabled}
                            />
                          )}
                        </div>

                        <div className="nr-divider" />

                        <LastUpdate
                          user={naspReporting.lastUpdate?.[r.report]?.user}
                          timestamp={naspReporting.lastUpdate?.[r.report]?.timestamp}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

            </div>
          </div>
        </CardContent>
      </Card>
    </Page>
  );
}

export default NaspReporting;
